import React, { useEffect, useRef, useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase-config';
import { useAuth } from '../context/AuthContext';

export default function PurgatoryPage() {
  const { currentUser } = useAuth();
  const audioRef = useRef(null);
  const [statusLines, setStatusLines] = useState([
    { text: 'Initializing Security Trace...', style: {} },
  ]);
  const [isIntruder, setIsIntruder] = useState(false);

  useEffect(() => {
    // Disable back button
    window.history.pushState(null, null, window.location.href);
    const blockBack = () => window.history.go(1);
    window.addEventListener('popstate', blockBack);

    async function getVisitorIP() {
      try {
        const res = await fetch('https://api.ipify.org?format=json');
        const data = await res.json();
        return data.ip;
      } catch {
        return 'Unknown Terminal';
      }
    }

    async function init() {
      const ip = await getVisitorIP();

      if (currentUser) {
        setStatusLines([
          { text: 'HOLD ONTO YOUR BUTTS!', style: { color: '#f97316', fontWeight: 'bold', textTransform: 'uppercase' } },
          { text: `ID: ${currentUser.email} — access not authorized.`, style: { fontSize: '0.875rem', marginTop: '8px' } },
          { text: `Terminal IP: ${ip}`, style: { fontSize: '10px', marginTop: '8px', opacity: 0.5 } },
        ]);
      } else {
        setIsIntruder(true);
        setStatusLines([
          { text: 'INTRUDER DETECTED', style: { color: '#ef4444', fontWeight: '900', textTransform: 'uppercase' } },
          { text: `Access attempt traced to IP: ${ip}`, style: { fontSize: '0.875rem', marginTop: '8px' } },
          { text: 'Attempt Logged to Security Grid', style: { fontSize: '10px', marginTop: '8px', textTransform: 'uppercase', opacity: 0.5 } },
        ]);
        // Security events are now logged server-side by Cloud Functions.
      }
    }

    init();

    return () => window.removeEventListener('popstate', blockBack);
  }, [currentUser]);

  function handleBodyClick(e) {
    if (e.currentTarget === e.target || !e.target.closest('.logout-btn')) {
      audioRef.current?.play().catch(() => {});
    }
  }

  async function handleLogout() {
    try {
      await signOut(auth);
      window.location.href = '/';
    } catch (err) {
      console.error('Logout failure:', err);
    }
  }

  return (
    <>
    {/* Black background — behind everything */}
    <div style={{ position: 'fixed', inset: 0, backgroundColor: '#000', zIndex: -1 }} />

    {/* Vortex layers — outside animated div so position:fixed works correctly */}
    <div style={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      <div className="vortex-bg" />
      <div className="vortex-bg2" />
      <div className="vortex-center" />
    </div>

    {/* Intruder red flash overlay */}
    {isIntruder && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', animation: 'flash-red 0.5s infinite' }} />
    )}

    <div
      onClick={handleBodyClick}
      style={{
        backgroundColor: 'transparent',
        color: '#ff0000',
        fontFamily: "'Courier New', Courier, monospace",
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <style>{`
        @keyframes flash-red {
          0%, 100% { background-color: #000; }
          50% { background-color: #440000; }
        }
        @keyframes wiggle {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(5deg); }
          75% { transform: rotate(-5deg); }
          100% { transform: rotate(0deg); }
        }
        .lock-wiggle {
          animation: wiggle 0.2s infinite;
          font-size: 5rem;
          display: inline-block;
        }
        .logout-btn {
          opacity: 0.1;
          transition: opacity 0.3s;
          background: none;
          border: none;
          color: #1f2937;
          font-size: 12px;
          cursor: pointer;
          font-family: 'Courier New', monospace;
          margin-top: auto;
          padding: 16px;
        }
        .logout-btn:hover { opacity: 1; }
        .nedry-container {
          position: relative;
          display: inline-block;
          width: 100%;
          max-width: 500px;
        }
        .nedry-gif {
          width: 100%;
          border: 4px solid #ff0000;
          box-shadow: 0 0 20px #ff0000;
          display: block;
        }
        @keyframes vortex-spin {
          0%   { transform: translate(-50%, -50%) rotate(0deg) scale(1); }
          50%  { transform: translate(-50%, -50%) rotate(180deg) scale(1.15); }
          100% { transform: translate(-50%, -50%) rotate(360deg) scale(1); }
        }
        @keyframes vortex-spin2 {
          0%   { transform: translate(-50%, -50%) rotate(0deg) scale(1.1); }
          100% { transform: translate(-50%, -50%) rotate(-360deg) scale(1.1); }
        }
        .vortex-bg {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 200vmax;
          height: 200vmax;
          z-index: 0;
          pointer-events: none;
          animation: vortex-spin 5s linear infinite;
          background: conic-gradient(
            from 0deg,
            #000 0deg,
            #000 15deg,
            #00ffff 20deg,
            #000 25deg,
            #000 55deg,
            #00cccc 60deg,
            #000 65deg,
            #000 95deg,
            #00ffff 100deg,
            #000 105deg,
            #000 135deg,
            #00cccc 140deg,
            #000 145deg,
            #000 175deg,
            #00ffff 180deg,
            #000 185deg,
            #000 215deg,
            #00cccc 220deg,
            #000 225deg,
            #000 255deg,
            #00ffff 260deg,
            #000 265deg,
            #000 295deg,
            #00cccc 300deg,
            #000 305deg,
            #000 355deg,
            #00ffff 360deg
          );
          mask-image: radial-gradient(circle, transparent 5%, black 30%, black 70%, transparent 100%);
          -webkit-mask-image: radial-gradient(circle, transparent 5%, black 30%, black 70%, transparent 100%);
        }
        .vortex-bg2 {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 120vmax;
          height: 120vmax;
          z-index: 0;
          pointer-events: none;
          animation: vortex-spin2 3s linear infinite;
          background: conic-gradient(
            from 45deg,
            #000 0deg,
            #000 25deg,
            #00ffff99 30deg,
            #000 35deg,
            #000 85deg,
            #00ffff99 90deg,
            #000 95deg,
            #000 145deg,
            #00ffff99 150deg,
            #000 155deg,
            #000 205deg,
            #00ffff99 210deg,
            #000 215deg,
            #000 265deg,
            #00ffff99 270deg,
            #000 275deg,
            #000 325deg,
            #00ffff99 330deg,
            #000 335deg,
            #000 360deg
          );
          mask-image: radial-gradient(circle, transparent 8%, black 25%, black 65%, transparent 100%);
          -webkit-mask-image: radial-gradient(circle, transparent 8%, black 25%, black 65%, transparent 100%);
        }
        .vortex-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 30vmin;
          height: 30vmin;
          z-index: 0;
          pointer-events: none;
          border-radius: 50%;
          background: radial-gradient(circle, #000 0%, #00ffff55 40%, transparent 70%);
          box-shadow: 0 0 60px 20px #00ffff33, 0 0 120px 40px #00ffff11;
        }
        @keyframes wiggle-head {
          0%   { transform: translateX(-50%) rotate(0deg); }
          25%  { transform: translateX(-50%) rotate(5deg); }
          75%  { transform: translateX(-50%) rotate(-5deg); }
          100% { transform: translateX(-50%) rotate(0deg); }
        }
        .superman-head {
          position: absolute;
          top: -8%;
          left: 50%;
          width: 22%;
          border-radius: 50%;
          border: 3px solid #ff0000;
          box-shadow: 0 0 12px #ff0000;
          z-index: 10;
          animation: wiggle-head 0.2s infinite;
          transform-origin: center bottom;
        }
      `}</style>

      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', padding: '32px 16px', position: 'relative', zIndex: 1 }}>
        <div className="lock-wiggle">🔒</div>

        <div className="nedry-container">
          <img
            src="/superman.jpg"
            className="superman-head"
            alt="The Boss"
          />
          <img
            src="https://media.giphy.com/media/uOAxshS5S9C3m/giphy.gif"
            className="nedry-gif"
            alt="Ah ah ah! You didn't say the magic word!"
          />
        </div>

        <h1 style={{
          fontSize: '1.875rem',
          fontWeight: 'bold',
          letterSpacing: '-0.05em',
          textTransform: 'uppercase',
          fontStyle: 'italic',
          margin: 0,
        }}>
          Access Denied
        </h1>

        <div style={{
          maxWidth: '448px',
          width: '100%',
          padding: '8px 16px',
          backgroundColor: 'rgba(127,0,0,0.2)',
          border: '1px solid #dc2626',
        }}>
          {statusLines.map((line, i) => (
            <p key={i} style={{ margin: i === 0 ? 0 : '8px 0 0', ...line.style }}>
              {line.text}
            </p>
          ))}
        </div>
      </div>

      <audio ref={audioRef} loop>
        <source src="https://www.myinstants.com/media/sounds/ah-ah-ah-you-didnt-say-the-magic-word.mp3" type="audio/mpeg" />
      </audio>

      <button className="logout-btn" onClick={handleLogout} style={{ position: 'relative', zIndex: 1 }}>
        [ TERMINAL LOGOUT ]
      </button>
    </div>
    </>
  );
}
